// MesMessagesDock.js — Languette flottante « Mes messages » (MP), même logique que « Mes groupes ».
// Visible seulement s'il y a AU MOINS un message privé non lu. Où qu'on soit dans l'app : voir ses
// conversations non lues + ouvrir la discussion (→ /compte?dm=<ami>). Sur mobile : regroupée dans le
// drawer social (mode="drawer"). Compteur = total de MP non lus (remonté au FAB via onCount).
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FiMessageSquare, FiChevronUp, FiChevronDown, FiX } from 'react-icons/fi';
import { theme } from '../theme';
import { useAuth } from '../context/AuthContext';
import { openChatWindow } from '../services/chatWindows';
import { getUnread, unreadFor, totalUnread, onDmChange } from '../services/dmNotify';
import LevelBadge from './LevelBadge';

// DESKTOP uniquement — entre « Chat communauté » (right:1.2rem) et « Mes groupes » (right:24.5rem).
const Dock = styled.div`
  position:fixed;z-index:1499;font-family:${theme.fonts.body};
  display:none;                                        /* mobile : caché → dans le drawer social */
  @media (min-width:768px){ display:block; right:14.5rem; bottom:1.2rem; }
`;
const Section = styled.div`font-family:${theme.fonts.body};`;
const SecHead = styled.div`display:flex;align-items:center;gap:.4rem;padding:.5rem .2rem;font-weight:700;font-size:.9rem;
  color:${theme.colors.textPrimary};border-bottom:1px solid ${theme.colors.border};`;
const Bar = styled.button`display:inline-flex;align-items:center;gap:.5rem;white-space:nowrap;cursor:pointer;position:relative;
  background:${theme.colors.gradient};color:#fff;border:none;border-radius:999px;
  padding:.6rem 1rem;font-weight:700;font-size:.86rem;box-shadow:${theme.shadows.card};
  .n{display:inline-flex;align-items:center;gap:.5rem;} .b{background:rgba(255,255,255,.25);border-radius:999px;padding:0 .45rem;font-size:.72rem;}
  .unb{position:absolute;top:-6px;right:-6px;min-width:18px;height:18px;padding:0 4px;border-radius:999px;background:#ef4444;color:#fff;
    font-size:.68rem;font-weight:800;display:inline-flex;align-items:center;justify-content:center;box-shadow:0 0 0 2px ${theme.colors.bgPrimary};}`;
const Panel = styled.div`position:absolute;bottom:calc(100% + .5rem);right:0;width:min(320px,86vw);
  background:${theme.colors.bgCard};border:1px solid ${theme.colors.borderHover};
  border-radius:14px;box-shadow:${theme.shadows.card};max-height:min(70vh,520px);overflow:hidden;display:flex;flex-direction:column;`;
const Head = styled.div`display:flex;align-items:center;justify-content:space-between;gap:.5rem;padding:.6rem .8rem;
  border-bottom:1px solid ${theme.colors.border};color:${theme.colors.textPrimary};font-weight:700;font-size:.88rem;
  button{background:none;border:none;color:${theme.colors.textMuted};cursor:pointer;display:inline-flex;}`;
const List = styled.div`overflow-y:auto;padding:.35rem;`;
const Row = styled.button`width:100%;text-align:left;display:flex;align-items:center;gap:.6rem;background:none;border:none;cursor:pointer;
  color:${theme.colors.textPrimary};padding:.55rem .6rem;border-radius:${theme.radii.md};font-size:.88rem;
  &:hover{background:${theme.colors.bgPrimary};}
  .av{width:30px;height:30px;border-radius:50%;background:${theme.colors.gradient};display:flex;align-items:center;justify-content:center;color:#fff;font-size:.8rem;flex-shrink:0;}
  .nm{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .unc{min-width:18px;height:18px;padding:0 5px;border-radius:999px;background:#ef4444;color:#fff;font-size:.7rem;font-weight:800;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;}`;

export default function MesMessagesDock({ mode = 'float', onCount }) {
  const { auth, isLoggedIn } = useAuth();
  const [friends, setFriends] = useState([]);
  const [open, setOpen] = useState(false);
  const [, setTick] = useState(0);   // re-render sur changement des non-lus (dmNotify)

  const load = useCallback(async () => {
    if (!isLoggedIn || !auth.enabled) { setFriends([]); return; }
    try { const fr = await auth.listFriends(0); setFriends(Array.isArray(fr) ? fr : []); } catch (e) { /* */ }
  }, [auth, isLoggedIn]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => onDmChange(() => setTick((t) => t + 1)), []);

  // Amis ayant au moins un MP non lu (source = dmNotify, alimenté globalement par DMNotifier).
  const unread = getUnread();
  const convos = friends
    .map((f) => f.user)
    .filter((u) => u && unreadFor(u.id || u.user_id) > 0)
    .map((u) => ({ u, uid: u.id || u.user_id, n: unreadFor(u.id || u.user_id) }));
  const total = totalUnread();
  useEffect(() => { if (onCount) onCount(total); }, [total, onCount]);

  // Chat instantané en fenêtre FLOTTANTE (pas de navigation vers l'espace membre).
  const openDm = (u) => openChatWindow(u);

  if (!isLoggedIn || total === 0 || convos.length === 0) return null;

  const rows = convos.map(({ u, uid, n }) => (
    <Row key={uid} onClick={() => openDm(u)}>
      <span className="av">{(u.display_name || u.username || '?').slice(0, 2).toUpperCase()}</span>
      <span className="nm">{u.display_name || u.username}</span>
      <LevelBadge userId={uid} />
      <span className="unc" title={`${n} message(s) non lu(s)`}>{n}</span>
    </Row>
  ));

  // MODE DRAWER (mobile) : section pleine largeur.
  if (mode === 'drawer') {
    return (
      <Section>
        <SecHead><FiMessageSquare /> Mes messages <span style={{ color: '#ef4444' }}>({total})</span></SecHead>
        <List>{rows}</List>
      </Section>
    );
  }

  // MODE FLOTTANT (desktop).
  return (
    <Dock>
      <Bar onClick={() => setOpen((o) => !o)}>
        <span className="n"><FiMessageSquare /> Mes messages <span className="b">{total}</span></span>
        {open ? <FiChevronDown /> : <FiChevronUp />}
        {!open && <span className="unb" title={`${total} message(s) non lu(s)`}>{total}</span>}
      </Bar>
      {open && (
        <Panel>
          <Head><span style={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem' }}><FiMessageSquare /> Mes messages ({total})</span>
            <button onClick={() => setOpen(false)} title="Fermer"><FiX /></button></Head>
          <List>{rows}</List>
        </Panel>
      )}
    </Dock>
  );
}
